
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import HeroSection from '@/components/HeroSection';
import ServiceCard from '@/components/ServiceCard';
import { services } from '@/data/services';
import { SITE } from '@/data/site';

const advantages = [
  { id: 1, title: 'Понад 10 років досвіду', description: 'Багаторічний досвід роботи з усіма марками автомобілів гарантує бездоганний результат.' },
  { id: 2, title: 'Гарантія на роботи', description: 'Надаємо офіційну гарантію на всі виконані роботи та матеріали.' },
  { id: 3, title: 'Сертифіковані матеріали', description: 'Використовуємо лише перевірені та сертифіковані клейові системи та автоскло.' },
  { id: 4, title: '15 років досвіду', description: 'Понад 15 років успішної роботи з автосклом легкових, вантажних автомобілів та спецтехніки.' },
  { id: 5, title: 'Автоскло для всіх марок', description: 'Великий склад автоскла в наявності для легкових, вантажних авто та спецтехніки.' },
  { id: 6, title: 'Швидкий запис на заміну', description: 'Оперативно реагуємо на заявки та підбираємо зручний для вас час.' }
];

function HomePage() {
  return (
    <div className="bg-background min-h-screen text-foreground flex flex-col">
      <Helmet>
        <title>Професійний сервіс автоскла в Києві | avtosklo-kiev.com</title>
        <meta name="description" content="Заміна та продаж автоскла в Києві. Лобове, бокове, заднє скло. Тонування. Швидко, якісно, з гарантією." />
      </Helmet>

      <Header />

      <main className="flex-1">
        {/* PREMIUM HERO SECTION */}
        <HeroSection />

        {/* НАШІ ПОСЛУГИ SECTION */}
        <section className="py-24 bg-background relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(0,61,122,0.1),transparent_50%)] pointer-events-none" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="text-3xl md:text-5xl font-bold mb-4 text-white">Наші послуги</h2>
                <p className="text-lg text-white/60 max-w-2xl mx-auto">
                  Професійний підхід до кожного автомобіля
                </p>
              </motion.div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <ServiceCard service={service} />
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* НАШІ РОБОТИ SECTION */}
        <section className="py-24 bg-[#141414]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 border-b border-white/10 pb-6">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold tracking-wider text-white mb-2">НАШІ РОБОТИ</h2>
                <p className="text-white/60">Галерея виконаних замовлень</p>
              </div>
              <Button variant="link" className="text-white/80 hover:text-white p-0 h-auto mt-4 md:mt-0" asChild>
                <Link to="/services" className="flex items-center gap-2">
                  Переглянути всі послуги
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {SITE.galleryWorks.map((work, index) => (
                <motion.div 
                  key={work.id}
                  whileHover={{ scale: 1.02 }}
                  className="relative aspect-[16/10] overflow-hidden rounded-xl group bg-white/5 border border-white/5 shadow-md"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <img 
                    src={work.url} 
                    alt={work.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 opacity-90 group-hover:opacity-100"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6 pointer-events-none">
                    <p className="text-white text-lg font-medium">{work.title}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* НАШІ ПЕРЕВАГИ SECTION */}
        <section className="py-24 bg-background relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="text-3xl md:text-5xl font-bold mb-4 text-white">Чому обирають нас</h2>
                <p className="text-lg text-white/60 max-w-2xl mx-auto">
                  Безкомпромісна якість та професіоналізм на кожному етапі
                </p>
              </motion.div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {advantages.map((adv, index) => (
                <motion.div
                  key={adv.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-[#141414] border border-white/5 rounded-2xl p-8 hover:bg-[#1e1e1e] hover:border-white/10 transition-all duration-300 group hover:-translate-y-1 hover:shadow-2xl hover:shadow-blue-900/5"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/40 transition-colors">
                      <CheckCircle2 className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-3 leading-snug group-hover:text-blue-400 transition-colors">
                        {adv.title}
                      </h3>
                      <p className="text-white/60 leading-relaxed text-sm sm:text-base">
                        {adv.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Call to Action CTA */}
        <section className="py-20 bg-primary text-primary-foreground relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('/images/hero-bg.jpg')] opacity-10 bg-cover bg-center mix-blend-overlay" />
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-balance">Довірте свій автомобіль професіоналам</h2>
              <p className="text-lg mb-8 opacity-90 leading-relaxed text-blue-100">
                Зателефонуйте нам для безкоштовної консультації або запишіться на зручний для вас час.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-white text-primary hover:bg-gray-100 h-14 px-8 text-lg rounded-xl shadow-xl transition-transform hover:scale-105" asChild>
                  <Link to="/contacts">
                    Отримати вартість
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 h-14 px-8 text-lg rounded-xl" asChild>
                  <a href={`tel:${SITE.phoneE164Primary}`}>
                    Подзвонити {SITE.phonePrimary}
                  </a>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default HomePage;
